import { css } from '@emotion/css';
import { Calendar, DollarSign, BarChart, Shield } from 'lucide-react';
import { ContactForm } from '../../../shared/components/contact-form';

const page = css({
  width: '100%',
  maxWidth: '1200px',
  margin: '0 auto',
  padding: 'var(--spacing-lg)',
  backgroundColor: 'var(--primary-color)',
  minHeight: 'calc(100vh - 100px)',
  '@media (max-width: 768px)': {
    padding: 'var(--spacing-md)',
  },
  '@media (max-width: 480px)': {
    padding: 'var(--spacing-sm)',
  },
});

const container = css({
  display: 'flex',
  flexDirection: 'column',
  gap: 'var(--spacing-xl)',
});

const header = css({
  marginBottom: 'var(--spacing-lg)',
  borderBottom: '2px solid var(--pc-200)',
  paddingBottom: 'var(--spacing-md)',
});

const title = css({
  fontSize: 'var(--fs-800)',
  fontWeight: 700,
  color: 'var(--pc-700)',
  marginBottom: 'var(--spacing-xs)',
  '@media (max-width: 768px)': {
    fontSize: 'var(--fs-700)',
  },
  '@media (max-width: 480px)': {
    fontSize: 'var(--fs-600)',
  },
});

const subtitle = css({
  fontSize: 'var(--fs-500)',
  color: 'var(--g-700)',
  fontWeight: 400,
  lineHeight: 1.6,
  '@media (max-width: 768px)': {
    fontSize: 'var(--fs-450)',
  },
  '@media (max-width: 480px)': {
    fontSize: 'var(--fs-400)',
  },
});

const section = css({
  marginBottom: 'var(--spacing-lg)',
});

const sectionTitle = css({
  fontSize: 'var(--fs-600)',
  fontWeight: 600,
  color: 'var(--pc-600)',
  marginBottom: 'var(--spacing-md)',
  paddingBottom: 'var(--spacing-xs)',
  borderBottom: '1px solid var(--pc-100)',
});

const benefitsGrid = css({
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
  gap: 'var(--spacing-lg)',
  marginTop: 'var(--spacing-lg)',
});

const benefitCard = css({
  padding: 'var(--spacing-lg)',
  backgroundColor: 'rgba(var(--pcrgba-100), 0.2)',
  borderRadius: 'var(--spacing-md)',
  border: '1px solid rgba(var(--pcrgba-200), 0.5)',
  display: 'flex',
  flexDirection: 'column',
  gap: 'var(--spacing-sm)',
});

const benefitIcon = css({
  color: 'var(--pc-600)',
  marginBottom: 'var(--spacing-xs)',
});

const benefitTitle = css({
  fontSize: 'var(--fs-500)',
  fontWeight: 600,
  color: 'var(--pc-700)',
  marginBottom: 'var(--spacing-2xs)',
});

const benefitText = css({
  fontSize: 'var(--fs-400)',
  color: 'var(--g-700)',
  lineHeight: 1.6,
});

const content = css({
  fontSize: 'var(--fs-400)',
  color: 'var(--g-800)',
  lineHeight: 1.8,
  marginBottom: 'var(--spacing-md)',
  '& ul': {
    listStyle: 'disc',
    paddingLeft: 'var(--spacing-xl)',
    marginTop: 'var(--spacing-sm)',
    '& li': {
      marginBottom: 'var(--spacing-xs)',
    },
  },
});

export function SellEvent() {
  return (
    <div className={page}>
      <div className={container}>
        <div className={header}>
          <h1 className={title}>Venda Seu Evento</h1>
          <p className={subtitle}>
            Amplifique o alcance do seu evento com a plataforma líder em venda de ingressos.
          </p>
        </div>

        <div className={section}>
          <h2 className={sectionTitle}>Por que escolher a Anchor?</h2>
          <div className={content}>
            <p>
              A Anchor Ingressos oferece a infraestrutura completa para que você venda ingressos do
              seu evento com segurança, praticidade e máximo alcance. Nossa plataforma é confiável,
              moderna e focada em resultados.
            </p>
          </div>

          <div className={benefitsGrid}>
            <div className={benefitCard}>
              <Calendar className={benefitIcon} size={40} />
              <h3 className={benefitTitle}>Gestão Simplificada</h3>
              <p className={benefitText}>
                Plataforma intuitiva para cadastro, configuração e gerenciamento completo do seu
                evento, tudo em um só lugar.
              </p>
            </div>

            <div className={benefitCard}>
              <DollarSign className={benefitIcon} size={40} />
              <h3 className={benefitTitle}>Pagamentos Seguros</h3>
              <p className={benefitText}>
                Sistema de pagamento integrado com as principais formas de pagamento, garantindo
                segurança e praticidade.
              </p>
            </div>

            <div className={benefitCard}>
              <BarChart className={benefitIcon} size={40} />
              <h3 className={benefitTitle}>Análise em Tempo Real</h3>
              <p className={benefitText}>
                Acompanhe as vendas, público e métricas importantes através de dashboards
                completos e relatórios detalhados.
              </p>
            </div>

            <div className={benefitCard}>
              <Shield className={benefitIcon} size={40} />
              <h3 className={benefitTitle}>Suporte Especializado</h3>
              <p className={benefitText}>
                Equipe dedicada para auxiliar em todas as etapas, desde a configuração até o
                pós-evento.
              </p>
            </div>
          </div>
        </div>

        <div className={section}>
          <h2 className={sectionTitle}>Como Funciona</h2>
          <div className={content}>
            <ul>
              <li>
                <strong>1. Cadastro:</strong> Crie sua conta de produtor e tenha acesso ao painel
                administrativo completo.
              </li>
              <li>
                <strong>2. Configuração:</strong> Adicione as informações do seu evento, defina
                lotes, preços e configure as opções de venda.
              </li>
              <li>
                <strong>3. Divulgação:</strong> Seu evento ganha visibilidade em nossa plataforma e
                pode ser divulgado em nossas redes sociais.
              </li>
              <li>
                <strong>4. Vendas:</strong> Comece a vender! Acompanhe tudo em tempo real através
                do seu painel de controle.
              </li>
              <li>
                <strong>5. Check-in:</strong> Utilize nosso sistema de validação de ingressos no
                dia do evento para controle total.
              </li>
            </ul>
          </div>
        </div>

        <div className={section}>
          <h2 className={sectionTitle}>Recursos Disponíveis</h2>
          <div className={content}>
            <ul>
              <li>Sistema de lotes com preços progressivos</li>
              <li>Vendas por PIX, cartão de crédito e débito</li>
              <li>Mapa de assentos numerados (quando aplicável)</li>
              <li>Código promocional e cupons de desconto</li>
              <li>Check-in via QR Code</li>
              <li>Relatórios completos de vendas e público</li>
              <li>Integração com redes sociais</li>
              <li>Suporte para vendas corporativas e grupos</li>
            </ul>
          </div>
        </div>

        <div className={section}>
          <h2 className={sectionTitle}>Comece Agora</h2>
          <div className={content}>
            <p>
              Interessado em vender seu evento conosco? Preencha o formulário abaixo com as
              informações do seu evento e entraremos em contato para dar início à parceria.
            </p>
          </div>
          <ContactForm defaultSubject="Quero Vender Meu Evento" />
        </div>
      </div>
    </div>
  );
}

export default SellEvent;
